import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient } from "@/services/core/apiClient";
import { useState } from "react";
import LoaderComponent from "@/components/common/LoaderComponent";

const formSchema = z.object({
  taskName: z.string().min(1, "Task Name is required"),
});

type FormData = z.infer<typeof formSchema>;

interface TaskFormProps {
  initialData?: {
    id?: string;
    taskName?: string;
  };
  isEdit?: boolean;
}

export const TaskForm = ({ initialData, isEdit = false }: TaskFormProps) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      taskName: initialData?.taskName || "",
    },
  });

  const onSubmit = async (data: FormData) => {
    // Check if task name hasn't changed
    if (isEdit && data.taskName === initialData?.taskName) {
      toast({
        title: "No Changes",
        description: "Task name is the same. No update needed.",
        variant: "destructive",
      });
      return;
    }

    if (!user?.id || !token) {
      toast({
        title: "Error",
        description: "User authentication required.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEdit && initialData?.id) {
        // Update task via PUT endpoint
        const endpoint = `/api/ngpep/${user.id}/taskstatus/${initialData.id}`;
        const response = await apiClient.put(
          endpoint,
          { taskName: data.taskName },
          token
        );

        if (response.success) {
          toast({
            title: "Task Updated",
            description: `Task "${data.taskName}" has been updated successfully.`,
          });
          navigate("/admin/workflow?tab=tasks");
        } else {
          throw new Error(response.error || "Failed to update task");
        }
      } else {
        // Add new task via POST endpoint
        const endpoint = `/api/ngpep/${user.id}/taskstatus`;
        const response = await apiClient.post(
          endpoint,
          { taskName: data.taskName },
          token
        );

        if (response.success) {
          toast({
            title: "Task Added",
            description: `Task "${data.taskName}" has been added successfully.`,
          });
          navigate("/admin/workflow?tab=tasks");
        } else {
          throw new Error(response.error || "Failed to add task");
        }
      }
    } catch (error) {
      console.error("Error saving task:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save task",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = () => {
    navigate("/admin/workflow?tab=tasks");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          {isEdit ? "Edit Task" : "Add New Task"}
        </h1>
        <p className="text-muted-foreground mt-2">
          {isEdit ? "Update task information" : "Create a new task"}
        </p>
      </div>

      {isSubmitting && <LoaderComponent size="lg" text="Updating task..." />}

      <div className="max-w-2xl">
        <Form {...form}>
          <form id="task_form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="taskName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Task Name</FormLabel>
                  <FormControl>
                    <Input 
                      id="task_form_name_input"
                      placeholder="Enter task name"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-6">
              <Button 
                id="task_form_cancel_btn"
                type="button" 
                variant="outline" 
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button id="task_form_submit_btn" type="submit" disabled={isSubmitting}>
                {isEdit ? "Update" : "Save"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};